import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Compass, 
  Users, 
  Heart, 
  Shield, 
  Handshake, 
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const HERO_IMAGE = "/assets/images/bhn.jpg";
const COMMUNITY_IMAGE = "https://images.unsplash.com/photo-1555069855-e580a9adbf43?crop=entropy&cs=srgb&fm=jpg&q=85";

const services = [
  {
    icon: Compass,
    title: "Navigation Assistance",
    description: "Guiding individuals through complex behavioral health systems to find the right resources and services."
  },
  {
    icon: Users,
    title: "Family Support",
    description: "Helping families understand and access support resources for their loved ones."
  },
  {
    icon: Heart,
    title: "Community Wellness",
    description: "Conducting wellness check-ins and connecting communities with behavioral health resources."
  },
  {
    icon: Shield,
    title: "Veteran Services",
    description: "Dedicated support for veterans navigating behavioral health and community resources."
  },
  {
    icon: Handshake,
    title: "Partnership Building",
    description: "Collaborating with organizations to strengthen behavioral health support networks."
  },
];

const stats = [
  { value: "500+", label: "Individuals Served" },
  { value: "50+", label: "Community Partners" },
  { value: "100%", label: "Commitment to Care" },
];

const Home = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-brand-blue to-brand-teal text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">

            <div className="space-y-6">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                Behavioral Health Navigation & Workforce Agency
              </h1>

              <p className="text-lg md:text-xl text-white/90 leading-relaxed">
                Guiding individuals and families through behavioral health systems while empowering a skilled support workforce within communities.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/request-support">
                  <Button className="bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg">
                    Request Support
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>

                <Link to="/what-we-do">
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-brand-blue rounded-full px-8 py-6 text-base font-semibold"
                  >
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>

            <div className="hidden md:block">
              <img 
                src={HERO_IMAGE}
                alt="Behavioral Health"
                className="rounded-2xl shadow-2xl object-cover w-full h-[400px]"
              />
            </div>

          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-6">
            Simplifying Access to Behavioral Health Resources
          </h2>

          <p className="text-lg text-gray-700 mb-6">
            We help individuals and families understand and access behavioral health services while strengthening the workforce that supports these systems.
          </p>

          <p className="text-gray-600">
            Many people struggle not because services are unavailable, but because they do not know how to access them. Our role is to simplify that journey and ensure no one is left behind.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-brand-neutral">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-4">
              Our Core Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="p-8 rounded-2xl shadow hover:shadow-lg transition">

                  <CardContent className="p-0">
                    <div className="w-14 h-14 bg-brand-teal/10 rounded-xl flex items-center justify-center mb-6">
                      <Icon size={28} className="text-brand-teal" />
                    </div>

                    <h3 className="text-xl font-bold text-brand-blue mb-3">
                      {service.title}
                    </h3>

                    <p className="text-gray-600">
                      {service.description}
                    </p>
                  </CardContent>

                </Card>
              );
            })}
          </div>

        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-brand-blue">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-3 gap-8 text-center">

          {stats.map((stat, index) => (
            <div key={index}>
              <div className="text-5xl font-bold text-brand-orange">
                {stat.value}
              </div>
              <div className="text-white/80">
                {stat.label}
              </div>
            </div>
          ))}

        </div>
      </section>

      {/* Community */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">

          <img 
            src={COMMUNITY_IMAGE}
            alt="Community"
            className="rounded-2xl shadow-xl object-cover w-full h-[400px]"
          />

          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue mb-4">
              Building Stronger Communities
            </h2>

            <p className="text-gray-600 mb-6">
              We ensure individuals never face behavioral health challenges alone by connecting them to the right support systems.
            </p>

            <ul className="space-y-4">
              {[
                "Behavioral health system navigation",
                "Family support guidance",
                "Community wellness checks",
                "Service connection",
                "Resource education"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="text-brand-teal mt-1" size={20} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

          </div>

        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-brand-teal to-brand-blue text-center text-white">

        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          Ready to Get Started?
        </h2>

        <p className="mb-8">
          Whether you need support or want to partner, we are here.
        </p>

        <div className="flex justify-center gap-4 flex-wrap">
          <Link to="/request-support">
            <Button className="bg-brand-orange px-8 py-6 rounded-full">
              Request Support
            </Button>
          </Link>

          <Link to="/partner-with-us">
            <Button variant="outline" className="border-white text-white px-8 py-6 rounded-full">
              Partner With Us
            </Button>
          </Link>
        </div>

      </section>

    </div>
  );
};

export default Home;